"use client";

import { renderModule } from "@/components/handyout-components/handyout-page";
import {
  buildIdToHeight,
  buildIdToSpec,
  keyFor,
  setMeasurement,
  uncachedModules,
} from "@/utils/moduleHeightCache";
import {
  MODULE_ITEM_ATTR,
  MODULE_ITEM_PARENT_ATTR,
  MODULE_SLICE_KEY,
  ModuleSplitItem,
  ModuleSplitSpec,
  isSplittableModule,
  sliceOf,
  slicesEqual,
} from "@/utils/moduleSplitContract";
import { SplitPage, splitPagesForViewport } from "@/utils/splitPages";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface UsePageSplittingOptions {
  pages: any[];
  containerWidth: number;
  viewportHeight: number;
  enabled?: boolean;
}

function moduleFieldsEqual(x: any, y: any): boolean {
  if (!x || !y) return x === y;
  const xKeys = Object.keys(x).filter((key) => key !== MODULE_SLICE_KEY);
  const yKeys = Object.keys(y).filter((key) => key !== MODULE_SLICE_KEY);
  if (xKeys.length !== yKeys.length) return false;
  for (const key of xKeys) {
    if (!Object.prototype.hasOwnProperty.call(y, key)) return false;
    if (x[key] !== y[key]) return false;
  }
  return true;
}

function splitPagesEqual(a: SplitPage[], b: SplitPage[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.slideId !== y.slideId ||
      x.title !== y.title ||
      x.pageIndex !== y.pageIndex ||
      x.totalPagesForSlide !== y.totalPagesForSlide ||
      x.isOverflowPage !== y.isOverflowPage
    ) {
      return false;
    }
    const xMods = x.modules ?? [];
    const yMods = y.modules ?? [];
    if (xMods.length !== yMods.length) return false;
    for (let j = 0; j < xMods.length; j++) {
      if (xMods[j] === yMods[j]) continue;
      if (xMods[j]?.id !== yMods[j]?.id) return false;
      if (!slicesEqual(sliceOf(xMods[j]), sliceOf(yMods[j]))) return false;
      if (!moduleFieldsEqual(xMods[j], yMods[j])) return false;
    }
  }
  return true;
}

const ROW_TOP_TOLERANCE = 2;

interface SplitState {
  pages: SplitPage[];
  source: any[] | null;
  containerWidth: number;
  viewportHeight: number;
}

const INITIAL_SPLIT_STATE: SplitState = {
  pages: [],
  source: null,
  containerWidth: 0,
  viewportHeight: 0,
};

function layoutScale(el: HTMLElement): number {
  const layoutHeight = el.offsetHeight;
  if (!layoutHeight) return 1;
  const scale = el.getBoundingClientRect().height / layoutHeight;
  return scale > 0 ? scale : 1;
}

function unscaledRect(rect: DOMRect, scale: number) {
  return {
    top: rect.top / scale,
    bottom: rect.bottom / scale,
    height: rect.height / scale,
  };
}

function measureSpec(
  root: HTMLElement,
  moduleType: string,
  moduleHeight: number,
): ModuleSplitSpec | undefined {
  if (!isSplittableModule(moduleType)) return undefined;

  const itemEls = Array.from(
    root.querySelectorAll<HTMLElement>(`[${MODULE_ITEM_ATTR}]`),
  ).sort(
    (a, b) =>
      Number(a.getAttribute(MODULE_ITEM_ATTR)) -
      Number(b.getAttribute(MODULE_ITEM_ATTR)),
  );
  if (itemEls.length === 0) return undefined;

  const scale = layoutScale(root);
  const rects = itemEls.map((el) => unscaledRect(el.getBoundingClientRect(), scale));
  const items: ModuleSplitItem[] = rects.map((rect) => ({
    height: rect.height,
  }));
  const blockTop = Math.min(...rects.map((r) => r.top));
  const blockBottom = Math.max(...rects.map((r) => r.bottom));
  const chrome = Math.max(0, moduleHeight - (blockBottom - blockTop));

  // Items sharing a top edge share a visual row. A table has one item per row
  // and so gets one row each, unchanged; a grid that flows into columns puts
  // its later items back into the first rows.
  const rowTops: number[] = [];
  for (const rect of rects) {
    if (!rowTops.some((top) => Math.abs(top - rect.top) <= ROW_TOP_TOLERANCE)) {
      rowTops.push(rect.top);
    }
  }
  rowTops.sort((a, b) => a - b);
  const rowOf = rects.map((rect) =>
    rowTops.findIndex((top) => Math.abs(top - rect.top) <= ROW_TOP_TOLERANCE),
  );

  let gap = 0;
  if (rowTops.length > 1) {
    const firstRowBottom = Math.max(
      ...rects.filter((_, i) => rowOf[i] === 0).map((r) => r.bottom),
    );
    const secondRowTop = Math.min(
      ...rects.filter((_, i) => rowOf[i] === 1).map((r) => r.top),
    );
    gap = Math.max(0, secondRowTop - firstRowBottom);
  }

  const parentAttrs = itemEls.map((el) => el.getAttribute(MODULE_ITEM_PARENT_ATTR));
  const parentOf = parentAttrs.some((value) => value !== null)
    ? parentAttrs.map((value) => {
        const parent = value === null ? -1 : Number(value);
        return Number.isInteger(parent) ? parent : -1;
      })
    : undefined;

  return parentOf
    ? { chrome, gap, items, rowOf, parentOf }
    : { chrome, gap, items, rowOf };
}

export function usePageSplitting({
  pages,
  containerWidth,
  viewportHeight,
  enabled = true,
}: UsePageSplittingOptions) {
  const [splitState, setSplitState] = useState<SplitState>(INITIAL_SPLIT_STATE);
  const [measuring, setMeasuring] = useState(false);
  const measureRef = useRef<HTMLDivElement | null>(null);

  const allModules = useMemo(
    () => pages.flatMap((p: any) => (p.modules || []).filter((m: any) => m.id)),
    [pages],
  );

  const pending = useMemo(
    () => uncachedModules(allModules, containerWidth),
    [allModules, containerWidth],
  );

  const needsMeasurement = pending.length > 0;

  const commitSplit = useCallback(
    (
      result: SplitPage[],
      source: any[],
      width: number,
      height: number,
    ) => {
      setSplitState((prev) => {
        const samePages = splitPagesEqual(prev.pages, result);
        if (
          samePages &&
          prev.source === source &&
          prev.containerWidth === width &&
          prev.viewportHeight === height
        ) {
          return prev;
        }
        return {
          pages: samePages ? prev.pages : result,
          source,
          containerWidth: width,
          viewportHeight: height,
        };
      });
    },
    [],
  );

  const splitFromCache = useCallback(() => {
    if (!enabled || viewportHeight <= 0) return;
    const idToHeight = buildIdToHeight(allModules, containerWidth);
    const idToSpec = buildIdToSpec(allModules, containerWidth);
    const result = splitPagesForViewport(
      pages,
      viewportHeight,
      idToHeight,
      idToSpec,
    );
    commitSplit(result, pages, containerWidth, viewportHeight);
  }, [allModules, commitSplit, containerWidth, enabled, pages, viewportHeight]);

  const measureAndSplit = useCallback(() => {
    if (!enabled || viewportHeight <= 0) return;

    if (allModules.length === 0) {
      const passthrough = pages.map((p: any) => ({
        slideId: p.slideId || "",
        pageIndex: 0,
        totalPagesForSlide: 1,
        title: p.title || "",
        modules: p.modules || [],
        isOverflowPage: false,
      }));
      commitSplit(passthrough, pages, containerWidth, viewportHeight);
      return;
    }

    if (!needsMeasurement) {
      splitFromCache();
      return;
    }

    if (!measureRef.current) return;

    setMeasuring(true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = measureRef.current;
        if (!container) return;

        const children = container.querySelectorAll("[data-module-id]");
        children.forEach((el) => {
          const id = el.getAttribute("data-module-id");
          if (!id) return;
          const mod = pending.find((m: any) => m.id === id);
          if (!mod) return;
          const height = (el as HTMLElement).offsetHeight;
          const spec = measureSpec(el as HTMLElement, mod.type, height);
          setMeasurement(keyFor(mod, containerWidth), {
            height,
            spec,
          });
        });

        const idToHeight = buildIdToHeight(allModules, containerWidth);
        const idToSpec = buildIdToSpec(allModules, containerWidth);
        const result = splitPagesForViewport(
          pages,
          viewportHeight,
          idToHeight,
          idToSpec,
        );
        commitSplit(result, pages, containerWidth, viewportHeight);
        setMeasuring(false);
      });
    });
  }, [
    allModules,
    commitSplit,
    containerWidth,
    enabled,
    needsMeasurement,
    pages,
    pending,
    splitFromCache,
    viewportHeight,
  ]);

  useEffect(() => {
    if (!enabled) return;

    if (needsMeasurement) {
      const timeout = setTimeout(measureAndSplit, 50);
      return () => clearTimeout(timeout);
    }
    splitFromCache();
  }, [enabled, needsMeasurement, measureAndSplit, splitFromCache]);

  useEffect(() => {
    if (!enabled) return;
    const handleResize = () => measureAndSplit();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [measureAndSplit, enabled]);

  const renderStateRef = useRef({ enabled, needsMeasurement, containerWidth, pending });
  renderStateRef.current = { enabled, needsMeasurement, containerWidth, pending };

  // Stable identity on purpose: a useCallback that changes with its inputs is a
  // new component type on every change, so React remounts the portal and the
  // measurement ref is null again by the time the deferred read runs.
  const MeasurementContainer = useCallback(() => {
    const state = renderStateRef.current;
    if (!state.enabled || typeof document === "undefined") return null;
    if (!state.needsMeasurement) return null;

    const el = (
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: -9999,
          left: -9999,
          visibility: "hidden",
          width: state.containerWidth || 348,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "0 20px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {state.pending.map((mod: any, i: number) => (
            <div key={mod.id} data-module-id={mod.id}>
              {renderModule(mod, i)}
            </div>
          ))}
        </div>
      </div>
    );

    return createPortal(el, document.body);
  }, []);

  const settled =
    enabled &&
    splitState.source === pages &&
    splitState.containerWidth === containerWidth &&
    splitState.viewportHeight === viewportHeight;

  return {
    splitPages: splitState.pages,
    settled,
    measuring,
    MeasurementContainer,
    remeasure: measureAndSplit,
  };
}
